import { Injectable } from '@angular/core';
import { CartItemViewModel } from '@core/models/cart/cart-item-view-model.model';
import { Cart } from '@core/models/cart/cart.model';
import { Customer } from '@core/models/customer/customer.model';
import { EventView } from '@core/models/event/event-view.model';
import { isBrowser } from '@interticket/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable()
export class CartStoreService {

  readonly cart$ = new BehaviorSubject<Cart>(null);
  readonly cartEvents$ = new BehaviorSubject<EventView[]>([]);
  readonly cartItems$ = new BehaviorSubject<CartItemViewModel[]>([]);
  readonly cartItemsGrouped$ = new BehaviorSubject<CartItemViewModel[][][]>(null);
  readonly cartSummaryItems$ = new BehaviorSubject<CartItemViewModel[]>([]);
  readonly cartSummaryItemsGrouped$ = new BehaviorSubject<CartItemViewModel[][][]>(null);

  private reset(): void {
    this.cartEvents = [];
    this.cartItems = [];
  }

  private groupCartItems(cartItems: CartItemViewModel[]): CartItemViewModel[][][] {
    if (!cartItems?.length) {
      return null;
    }

    const groupedByEventId: CartItemViewModel[][] = this.groupCartItemsBy(cartItems, 'eventId');
    const groupedByEventIdAndPriceCategoryId: CartItemViewModel[][][] = groupedByEventId.map(
      group => this.groupCartItemsBy(group, 'priceCategoryId')
    );

    const groupedByEventIdAndPriceCategoryIdSplitByDiscount = groupedByEventIdAndPriceCategoryId.map(
      eventGroup => eventGroup.map(
        eventPriceDiscountGroup => this.groupCartItemsBy(eventPriceDiscountGroup, 'discountId')
      ).reduce(
        (eventPriceGroup, eventItemGroup) => eventPriceGroup.concat(eventItemGroup), [])
    );
    return groupedByEventIdAndPriceCategoryIdSplitByDiscount;
  }

  private groupCartItemsBy(cartItems: CartItemViewModel[], groupBy: keyof CartItemViewModel): CartItemViewModel[][] {
    const sortedItems = [...cartItems].sort((a, b) =>
      (a[groupBy] === undefined && b[groupBy] !== undefined) || a[groupBy] < b[groupBy] ? -1 : 1
    );
    const cartItemsGrouped: CartItemViewModel[][] = sortedItems.reduce(
      (groups, item) => {
        const lastGroup = groups[groups.length - 1];
        lastGroup[0][groupBy] === item[groupBy]
          ? lastGroup.push(item)
          : groups.push([item]);

        return groups;
      },
      [[sortedItems.shift()]]
    );

    return cartItemsGrouped;
  }

  setCart(cart: Cart) {
    if (!cart) {
      this.cart$.next(null);
      return this.reset();
    }

    this.cart$.next(new Cart(cart));
  }

  setCustomer(customer: Customer) {
    this.setCart({
      ...this.cart,
      customer,
    });
  }

  get cart(): Cart {
    return this.cart$.getValue();
  }

  set cartEvents(cartEvents: EventView[]) {
    this.cartEvents$.next(cartEvents);
  }

  get cartEvents(): EventView[] {
    return this.cartEvents$.getValue() || [];
  }

  get cartItems(): CartItemViewModel[] {
    return this.cartItems$.getValue() || [];
  }

  set cartItems(cartItems: CartItemViewModel[]) {
    this.cartItems$.next(cartItems);
    this.cartItemsGrouped$.next(this.groupCartItems(cartItems));
  }

  set cartSummaryItems(cartItems: CartItemViewModel[]) {
    this.cartSummaryItems$.next(cartItems);
    this.cartSummaryItemsGrouped$.next(this.groupCartItems(cartItems));
  }

  get cartItemsGrouped(): CartItemViewModel[][][] {
    return this.cartItemsGrouped$.getValue();
  }

  get discountedTickets(): CartItemViewModel[] {
    return this.cartItems.filter(item => item.discountId);
  }

  getCartTimer(): Observable<string> {
    return (isBrowser ? timer(1000, 1000) : of(null))
      .pipe(
        map(() => {
          if (!this.cart) {
            return null;
          }

          const expiredDateTime: DateTime = DateTime.fromISO(this.cart.expiredTime).plus({ seconds: 6 });
          const cartTimer: string = expiredDateTime.diffNow().toFormat('hh:mm:ss');

          return cartTimer;
        }),
        filter(time => time !== null && !time.includes('-'))
      );
  }

}
